import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, useLocation, useNavigate } from "react-router-dom";
import AppRoutes from "./routes/AppRoutes.tsx";
import { ToastProvider } from "./context/ToastContext.tsx";
import { ToastContainer } from "react-toastify";
import SessionExpiredModal from "./components/common/SessionExpiredModal.tsx";
import {
  sessionExpired$,
  resetSessionTimer,
} from "./Services/axiosInstance.ts";
import "react-toastify/dist/ReactToastify.css";
import { keycloak } from "./components/login/keycloakConfig.tsx";
import { SSEProvider } from "./context/SSEContext.tsx";

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    sessionStorage.getItem("isAuthenticated") === "true"
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const [kcReady, setKcReady] = useState(false);
  const [keycloakInitialized, setKeycloakInitialized] = useState(false);

  const shouldInitializeKeycloak = () => {
    const environment = localStorage.getItem('environment');
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has('code');
    const isCallbackRoute = location.pathname === '/keycloak/callback';

    // Initialize Keycloak only on callback route with code and Keycloak environment
    return ((environment === 'Keycloak' || environment === "Azure" || environment === "Google") && hasCode && isCallbackRoute);
  };

  useEffect(() => {
    const htmlElement = document.documentElement;

    // Set lang attribute
    htmlElement.setAttribute('lang', 'en');

    // Set data attributes
    htmlElement.dataset.layoutStyle = "default";
    htmlElement.dataset.sidebarSize = "lg";
    htmlElement.dataset.sidebar = "dark";
    htmlElement.dataset.bsTheme = "light";
    htmlElement.dataset.layoutWidth = "fluid";
    htmlElement.dataset.layoutPosition = "fixed";
    htmlElement.dataset.topbar = "light";
    htmlElement.dataset.layout = "vertical";
    htmlElement.dataset.sidebarImage = "none";
    htmlElement.dataset.sidebarVisibility = "show";

    // Cleanup (optional - removes attributes on unmount)
    return () => {
      delete htmlElement.dataset.layoutStyle
      delete htmlElement.dataset.sidebarSize
      delete htmlElement.dataset.sidebar
      delete htmlElement.dataset.bsTheme
      delete htmlElement.dataset.layoutWidth
      delete htmlElement.dataset.layoutPosition
      delete htmlElement.dataset.topbar
      delete htmlElement.dataset.layout
      delete htmlElement.dataset.sidebarImage
      delete htmlElement.dataset.sidebarVisibility
    };
  }, []);


  useEffect(() => {
    // If we don't need Keycloak, just mark as ready and skip initialization
    if (!shouldInitializeKeycloak()) {
      setKcReady(true);
      setKeycloakInitialized(true);
      return;
    }

    if (keycloakInitialized || keycloak.didInitialize) {
      setKcReady(true);
      setKeycloakInitialized(true);

      // If already authenticated, set the state
      if (keycloak.authenticated) {
        setIsAuthenticated(true);
      }

      return;
    }

    keycloak
      .init({
        onLoad: "check-sso",
        pkceMethod: "S256",
        checkLoginIframe: false,
        enableLogging: true,
      })
      .then((auth) => {
        setKeycloakInitialized(true);
        setKcReady(true);

        if (!auth) {
          if (location.pathname === '/keycloak/callback') {
            navigate("/signIn", { replace: true });
          }
          return;
        }

        // User authenticated via Keycloak SSO
        sessionStorage.setItem("access_token", keycloak.token);
        sessionStorage.setItem("refresh_token", keycloak.refreshToken);
        sessionStorage.setItem("id_token", keycloak.idToken);
        sessionStorage.setItem("isAuthenticated", "true");
        sessionStorage.setItem("redirectedFromUMP", "false");

        // Get username from token if available
        if (keycloak.tokenParsed) {
          const username = keycloak.tokenParsed.preferred_username || keycloak.tokenParsed.email;
          const userId = keycloak.tokenParsed.sub;

          if (username) {
            sessionStorage.setItem("userName", username);
          }
          if (userId) {
            sessionStorage.setItem("userId", userId);
          }
        }

        setIsAuthenticated(true);
      })
      .catch((err) => {
        console.error("Keycloak init failed:", err);
        setKcReady(true);
        setKeycloakInitialized(true);

        // If on callback route and init failed, redirect to sign in
        if (location.pathname === '/keycloak/callback') {
          navigate("/signIn", { replace: true });
        }
      });
  }, [keycloakInitialized, location.pathname, navigate]);

  useEffect(() => {
    const subscription = sessionExpired$.subscribe((expired) => {
      if (expired && location.pathname !== "/signIn") {
        setSessionExpired(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [location.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      ["mousemove", "keydown", "click"].forEach((event) =>
        window.addEventListener(event, resetSessionTimer)
      );
      resetSessionTimer();
    }
    return () => {
      ["mousemove", "keydown", "click"].forEach((event) =>
        window.removeEventListener(event, resetSessionTimer)
      );
    };
  }, [isAuthenticated]);

  const handleSessionExpiredClose = () => {
    setSessionExpired(false);
    if (location.pathname !== "/signIn") {
      if (sessionStorage.getItem("redirectedFromUMP") === "true") {
        sessionStorage.clear();
        window.location.href = `${window.env.REACT_APP_UMP_REDIRECT_URL}/home`;
      } else {
        setIsAuthenticated(false);
        sessionStorage.removeItem("isAuthenticated");

        const environment = localStorage.getItem('environment');
        if ((environment === 'Keycloak' || environment === 'Azure' || environment === "Google") && keycloak.authenticated) {
          keycloak.logout({
            redirectUri: window.location.origin + '/signIn'
          });
        } else {
          window.location.href = "/signIn";
        }
      }
    }
  };

  // Token refresh interval - only if Keycloak is initialized and authenticated
  useEffect(() => {
    if (!keycloakInitialized || !keycloak.authenticated) {
      return;
    }

    const intervalId = setInterval(() => {
      keycloak.updateToken(600000) // Refresh if expires in 70 seconds
        .then(refreshed => {
          if (refreshed) {
            sessionStorage.setItem("access_token", keycloak.token);
            sessionStorage.setItem("refresh_token", keycloak.refreshToken);
          }
        })
        .catch(() => {
          console.error("Token refresh failed");
          keycloak.logout();
        });
    }, 500000); // Check every 60 seconds

    return () => {
      clearInterval(intervalId);
    };
  }, [keycloakInitialized]);

  useEffect(() => {
    sessionStorage.setItem("isAuthenticated", JSON.stringify(isAuthenticated));

    if (sessionStorage.getItem("access_token") && sessionStorage.getItem("redirectedFromUMP") === "true" && sessionStorage.getItem("isRedirected") !== "true") {
      sessionStorage.setItem("isRedirected", "true");
      navigate("/TemplateList");
    }
    if (isAuthenticated && sessionStorage.getItem("redirectedFromUMP") === "true") {
      window.opener?.postMessage("requestToken", window.env.REACT_APP_UMP_REDIRECT_URL);
      const tokenHandler = (event) => {
        if (event.origin === window.env.REACT_APP_UMP_REDIRECT_URL && event.data.token) {
          sessionStorage.setItem("access_token", event.data.token);
          sessionStorage.setItem("refresh_token", event.data.refreshToken);
          sessionStorage.setItem("redirectedFromUMP", event.data.redirectedFromUMP || "true");
          sessionStorage.setItem("userName", event.data?.userName)
          window.dispatchEvent(new Event("userNameUpdated"));
        }
      };

      window.addEventListener("message", tokenHandler);
      return () => window.removeEventListener("message", tokenHandler);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const isRedirected = sessionStorage.getItem("redirectedFromUMP") === "true";
    if (!isRedirected) return;

    let isPageBeingRefreshed = false;

    const handlePageHide = (event) => {
      if (event.persisted) {
        return;
      }

      if (!isPageBeingRefreshed) {
        sessionStorage.clear();
      }
    };

    const handleBeforeUnload = () => {
      isPageBeingRefreshed = true;
    };

    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Only navigate to TemplateList if Keycloak authenticated the user
  useEffect(() => {
    if (kcReady && keycloak.authenticated && keycloakInitialized && location.pathname === "/keycloak/callback") {
      navigate("/TemplateList", { replace: true });
    }
  }, [kcReady, keycloak.authenticated, keycloakInitialized, navigate]);

  return (
    <>
      {/* <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      /> */}

      <SessionExpiredModal
        open={sessionExpired}
        onClose={handleSessionExpiredClose}
      />

      <AppRoutes
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
      />
    </>
  );
};

const App = () => {
  return (
    // <ToastProvider>
      <SSEProvider>
        <Router>
          <AppContent />
        </Router>
      </SSEProvider>
    // </ToastProvider>
  );
};

export default App;
